import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import { useHasPermission } from '@/hooks/use-has-permission';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { DEFAULT_ORGANIZATION_ID } from '@/lib/auth/org-access';
import { useAuthStore } from '@/state/store/auth';
import {
  createEmployee,
  getDesignationsLookup,
  getEmployeeById,
  getEmployeeByUserId,
  getEmployeeCurrentWorkDays,
  getEmployees,
  getLoginActive,
  saveMyProfileDetail,
  setEmployeeActive,
  updateEmployeeBasicInfo,
  updateEmployeeContactInfo,
  updateEmployeeHrInfo,
  updateEmployeeRoles,
  updateEmployeeWorkStatus,
  updateMyBasicInfo,
  updateMyAccountDetails,
  updateMyContactInfo,
  type MyAccountDetails,
} from '../services/employee.service';
import { getDeactivatedUserIds } from '../services/employee-account.service';
import { WorkStatusChanges } from '../schemas/work-status';
import {
  Employee,
  EmployeeCreateInput,
  EmployeesPagingResult,
  GetEmployeesParams,
  HrTermInput,
  OrganizationRoleAssignment,
} from '../types/employee.types';

const invalidateEmployeeQueries = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: ['hr-employees'] });

export const useGetEmployees = (params: GetEmployeesParams) => {
  return useGlobalQuery({
    queryKey: ['hr-employees', params],
    queryFn: () => getEmployees(params),
    staleTime: 5 * 60 * 1000,
  });
};

/** The employee's work days today in their current HR term (read only while `userId` is set). */
export const useEmployeeCurrentWorkDays = (userId?: string) =>
  useGlobalQuery({
    queryKey: ['hr-employees', 'current-work-days', userId],
    queryFn: () => getEmployeeCurrentWorkDays(userId ?? ''),
    enabled: Boolean(userId),
  });

const LOGIN_ACTIVE_KEY = 'hr-employee-login-active';

/**
 * The directory rows with whether each person's login is active, read from IAM per person (IAM cannot read
 * several users at once). The rows show at once; each status fills in when known and stays cached, so
 * paging back or refreshing the list after an edit asks IAM again only when the cache is stale.
 */
const NO_EMPLOYEES: Employee[] = [];

/** The user ids of deactivated logins (People › Alumni). Under the login-active key, so (re)activating
 * someone refreshes it. */
export const useDeactivatedUserIds = () =>
  useGlobalQuery({
    queryKey: [LOGIN_ACTIVE_KEY, 'deactivated'],
    queryFn: getDeactivatedUserIds,
    staleTime: 5 * 60 * 1000,
  });

export const useEmployeesLoginActive = (employees: Employee[] = NO_EMPLOYEES): Employee[] => {
  const activeStates = useQueries({
    queries: employees.map((employee) => ({
      queryKey: [LOGIN_ACTIVE_KEY, employee.UserId ?? null],
      queryFn: () => getLoginActive(employee.UserId),
      staleTime: 5 * 60 * 1000,
    })),
    // `undefined` only while the login is being read (the Status cell shows a placeholder); a failed
    // read counts as active, as it always did.
    combine: (results) =>
      results.map((result) => (result.isLoading ? undefined : (result.data ?? true))),
  });
  return useMemo(
    () => employees.map((employee, index) => ({ ...employee, IsActive: activeStates[index] })),
    [employees, activeStates]
  );
};

export const useGetEmployeeById = (itemId?: string) => {
  return useGlobalQuery({
    queryKey: ['hr-employee', itemId],
    queryFn: () => getEmployeeById(itemId as string),
    enabled: Boolean(itemId),
  });
};

export const useGetDesignationsLookup = () => {
  return useGlobalQuery({
    queryKey: ['hr-designations-lookup'],
    queryFn: () => getDesignationsLookup(),
    staleTime: 30 * 60 * 1000,
  });
};

export const useCreateEmployee = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();
  const requiresExtendedInfo = useHasPermission(HRM_PERMISSIONS.PEOPLE.REQUIRE_EXTENDED_INFO);
  // The creator's current organization; the IAM `default` placeholder is not a real one, so IAM picks.
  const selectedOrgId = useAuthStore((state) => state.selectedOrgId);
  const organizationId =
    selectedOrgId && selectedOrgId !== DEFAULT_ORGANIZATION_ID ? selectedOrgId : undefined;

  return useGlobalMutation({
    mutationFn: (input: EmployeeCreateInput) =>
      createEmployee(input, { requiresExtendedInfo, organizationId }),
    onSuccess: () => {
      invalidateEmployeeQueries(queryClient);
      toast({ title: t('EMPLOYEE_CREATED') });
    },
  });
};

/**
 * One HR save of an existing employee (an edit tab or the role dialog). The edit/details pages read
 * `['hr-employee', id]`, which `['hr-employees']` does not match, so both are refreshed.
 */
const useEmployeeSave = <TParams, TData = unknown>(
  mutationFn: (params: TParams) => Promise<TData>
) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn,
    onSuccess: async () => {
      invalidateEmployeeQueries(queryClient);
      toast({ title: t('EMPLOYEE_UPDATED') });
      await queryClient.invalidateQueries({ queryKey: ['hr-employee'] });
    },
  });
};

/** "Overview" tab: basic info on `UserProfileDetail`. */
export const useUpdateEmployeeBasicInfo = () =>
  useEmployeeSave((params: { employee: Employee; input: Record<string, unknown> }) =>
    updateEmployeeBasicInfo(params)
  );

/** "Contact Info" tab: business email / phone on the profile, the rest on `UserContactDetail`. */
export const useUpdateEmployeeContactInfo = () =>
  useEmployeeSave((params: { employee: Employee; input: Record<string, unknown> }) =>
    updateEmployeeContactInfo(params)
  );

/** "HR Info" tab: the `UserHRPeriodicalProfile` of every HR term. */
export const useUpdateEmployeeHrInfo = () =>
  useEmployeeSave((params: { employee: Employee; terms: HrTermInput[] }) =>
    updateEmployeeHrInfo(params)
  );

/** "Assign role" dialog on the directory list. */
export const useUpdateEmployeeRoles = () =>
  useEmployeeSave(
    (params: { employee: Employee; organizationAccess: OrganizationRoleAssignment[] }) =>
      updateEmployeeRoles(params)
  );

/** The signed-in person's own record: their `UserProfileDetail` (matched by IAM `UserId`) with contact details, HR terms and roles. */
export const useGetMyEmployee = () => {
  const userId = useAuthStore((state) => state.user?.itemId);

  return useGlobalQuery({
    queryKey: ['hr-employee', 'me', userId],
    queryFn: () => getEmployeeByUserId(userId as string),
    enabled: Boolean(userId),
  });
};

/**
 * Marks the signed-in person's cached record as completed, so the profile-completion gate lets them
 * through immediately, without waiting for (or depending on) a refetch.
 */
export const useMarkMyProfileCompleted = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.setQueriesData<{ Employees?: EmployeesPagingResult }>(
      { queryKey: ['hr-employee', 'me'] },
      (data) =>
        data?.Employees
          ? {
              ...data,
              Employees: {
                ...data.Employees,
                items: data.Employees.items.map((item) => ({ ...item, IsProfileCompleted: true })),
              },
            }
          : data
    );
};

/** One self-service save ("Edit profile" tab or the account dialog). The login user's name, phone and photo may follow. */
const useMyProfileSave = <TParams>(mutationFn: (params: TParams) => Promise<unknown>) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn,
    onSuccess: async () => {
      invalidateEmployeeQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: ['getAccount'] });
      toast({ title: t('PROFILE_UPDATED') });
      // Awaited so the profile-completion gate sees `IsProfileCompleted` before the caller navigates.
      await queryClient.invalidateQueries({ queryKey: ['hr-employee'] });
    },
  });
};

type MyProfileSaveParams = { employee: Employee; input: Record<string, unknown> };

export const useUpdateMyBasicInfo = () => useMyProfileSave<MyProfileSaveParams>(updateMyBasicInfo);

export const useUpdateMyContactInfo = () =>
  useMyProfileSave<MyProfileSaveParams>(updateMyContactInfo);

/** Skills are basic info: they are saved on the profile without touching the login user. */
export const useUpdateMySkills = () => useMyProfileSave<MyProfileSaveParams>(saveMyProfileDetail);

/** The profile page's account dialog: name, phone and photo on the login user and the person's profile. */
export const useUpdateMyAccount = () =>
  useMyProfileSave((params: { employee?: Employee; details: MyAccountDetails }) =>
    updateMyAccountDetails(params)
  );

export const useSetEmployeeActive = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: { employee: Employee; isActive: boolean }) => setEmployeeActive(params),
    onSuccess: () => {
      invalidateEmployeeQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: [LOGIN_ACTIVE_KEY] });
      queryClient.invalidateQueries({ queryKey: ['hr-employee'] });
      toast({ title: t('EMPLOYEE_UPDATED') });
    },
  });
};

/** The work from home switch (and office days) of one employee, changed from the directory list. */
export const useUpdateEmployeeWorkStatus = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: { employee: Employee; changes: WorkStatusChanges }) =>
      updateEmployeeWorkStatus(params),
    onSuccess: () => {
      invalidateEmployeeQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: ['hr-employee'] });
      toast({ title: t('EMPLOYEE_UPDATED') });
    },
  });
};
